import { action, computed, observable, makeObservable } from 'mobx';
import { V3Routes } from '../../../routes';
import { SubmitState } from '../../../../commonv3/states/SubmitState';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { MembershipItemIndexView, MembershipPlanIndexView, MembershipPlanKey } from '../../../types/membershipPlans';
import { MembershipItemId } from '../../../../adminv3/types/item';
import { MembershipPlanApis } from '../../../apis/membershipPlans';
import { MembershipPlanId } from '../../../../commonv3/types/membershipPlans';
import { CreditCardState } from '../../../components/molecules/payment/states/CreditCardFormState';
import { CurrentUserState } from '../../../../mainv2/states/CurrentUserState';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { MembershipTokenIndexView } from '../../../types/membershipTokens';
import { MembershipUpdateRequestApis } from '../../../apis/membershipUpdateRequests';
import { SubscriptionSummary } from '../../../types/subscriptionSummary';
import { SubscriptionSummariesApis } from '../../../apis/subscriptionSummaries';
import { Service } from '../../../../commonv3/types/service';

export class MembershipPlanUpdateState {
  cardState: CreditCardState = null;
  submitState = new SubmitState();
  initState = new SubmitState();
  plans: RemoteResourceV3<MembershipPlanIndexView[]> = RemoteResourceV3.loading();
  membershipTokens: RemoteResourceV3<MembershipTokenIndexView[]> = RemoteResourceV3.loading();
  confirmedLessonDeactivation = false;
  currentItemId = null;
  selectedPlanId = null;
  selectedItemId = null;
  entranceInstallments = 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any = null;
  subscriptionSummaries: RemoteResourceV3<SubscriptionSummary[]> = RemoteResourceV3.loading();

  constructor() {
    makeObservable(this, {
      submitState: observable,
      initState: observable,
      plans: observable,
      membershipTokens: observable,
      confirmedLessonDeactivation: observable,
      currentItemId: observable,
      selectedPlanId: observable,
      selectedItemId: observable,
      entranceInstallments: observable,
      error: observable,
      subscriptionSummaries: observable,
      init: action.bound,
      currentSubscriptionSummary: computed,
      selectPlan: action.bound,
      setConfirmedLessonDeactivation: action.bound,
      selectedItem: computed,
      selectedPlan: computed,
      selectItem: action.bound,
      validatePlanAndNext: action.bound,
      validateCardAndNext: action.bound,
      submit: action.bound,
    });
  }

  async init(userState: CurrentUserState, currentItemId: MembershipItemId) {
    this.currentItemId = currentItemId;
    this.cardState = new CreditCardState(userState);
    await this.initState.operate(async () => {
      await Promise.all([this.reloadPlans(), this.reloadSubscriptionSummaries()]);
      await this.cardState.init();
    });
    GlobalStatesV3.routing.replace(V3Routes.planUpdatePlanSelect(this.currentItemId));
  }

  async reloadPlans() {
    const plans = await updateRemoteResourceV3(
      this.plans,
      (v) => (this.plans = v),
      () => MembershipPlanApis.findAll(false)
    );
    const regularPlan = plans.find((p) => p.key === MembershipPlanKey.REGULAR);
    if (regularPlan) {
      this.selectPlan(regularPlan.id);
    }
  }

  async reloadSubscriptionSummaries() {
    await updateRemoteResourceV3(
      this.subscriptionSummaries,
      (v) => (this.subscriptionSummaries = v),
      () => SubscriptionSummariesApis.findAll([Service.LIKES])
    );
  }

  get currentSubscriptionSummary(): SubscriptionSummary {
    return this.subscriptionSummaries
      .getOrElse([])
      .find((s) => s.current_period_membership_item_id === this.currentItemId);
  }

  selectPlan(planId: MembershipPlanId) {
    this.selectedPlanId = planId;
    if (!this.selectedPlan) {
      return;
    }
    if (this.selectedPlan.items.length === 1) {
      this.selectedItemId = this.selectedPlan.items[0].id;
    }
    if (this.selectedPlan.lesson_deactivation) {
      this.confirmedLessonDeactivation = false;
    }
  }

  setConfirmedLessonDeactivation(value: boolean) {
    this.confirmedLessonDeactivation = value;
  }

  get selectedItem(): MembershipItemIndexView {
    if (!this.selectedPlan) {
      return null;
    }
    return this.selectedPlan.items.find((i) => i.id === this.selectedItemId);
  }

  get selectedPlan(): MembershipPlanIndexView {
    return this.plans.getOrElse([]).find((p) => p.id === this.selectedPlanId);
  }

  selectItem(itemId: MembershipItemId) {
    this.selectedItemId = itemId;
  }

  validatePlanAndNext() {
    GlobalStatesV3.routing.push(V3Routes.planUpdatePaymentMethod(this.currentItemId));
  }

  async validateCardAndNext() {
    await this.submitState.operate(async () => {
      if (this.cardState.editMode) {
        if (!(await this.cardState.validateNewCard())) {
          return;
        }
      }
      this.error = null;
      GlobalStatesV3.routing.push(V3Routes.planUpdateConfirm(this.currentItemId));
    });
  }

  async submit() {
    await this.submitState.operate(async () => {
      try {
        if (this.cardState.newToken) {
          await this.cardState.update();
        }
        await MembershipUpdateRequestApis.create(this.currentItemId, this.selectedItemId);
        GlobalStatesV3.routing.push(V3Routes.plan());
      } catch (e) {
        this.error = true;
      }
    });
  }
}
